/**
 * Audio-reactive lip-sync for the Sumerian Host.
 *
 * This character is BONE-driven (skeleton "Armature", 138 bones) with no morph
 * targets, so visemes are produced by rotating the jaw bone, not blendshapes.
 * We drive "char:def_c_jaw" open/closed from the live WebRTC audio amplitude.
 *
 * The host's animation features write bone rotations every frame, so we apply
 * our jaw rotation on scene.onBeforeRenderObservable — which fires AFTER the
 * animation step but BEFORE the skeleton matrices are computed for render — so
 * our value wins for the frame.
 */
import { Vector3 } from "@babylonjs/core/Maths/math.vector";

const JAW_BONE = "char:def_c_jaw";
const MAX_OPEN = 0.16;   // radians of jaw pitch at a loud peak (0.34+ looks like a gape)

export class AudioMouthSync {
  constructor(host, options = {}) {
    this.host = host;
    this.scene = options.scene || (host && host._scene) || null;
    this.audioCtx = null;
    this.analyser = null;
    this.source = null;
    this.dataArray = null;
    this.renderObserver = null;
    this.jaw = null;
    this.jawBaseX = 0;
    this.open = 0;             // smoothed openness
    this.onSpeechStart = options.onSpeechStart || null;
    this.onSpeechEnd = options.onSpeechEnd || null;
    this._prevOpen = 0;
    this._speaking = false;
    this._spoke = false;
    this._lastVoiceTs = 0;
  }

  _findJaw() {
    if (!this.scene || !this.scene.skeletons || !this.scene.skeletons.length) return null;
    const sk = this.scene.skeletons[0];
    let bone = sk.bones.find(b => b.name === JAW_BONE);
    if (!bone) bone = sk.bones.find(b => /jaw/i.test(b.name) && /_c_|center|^jaw/i.test(b.name));
    if (!bone) bone = sk.bones.find(b => /jaw/i.test(b.name));
    if (bone) {
      const r = bone.getRotation();   // LOCAL
      this.jawBaseX = r ? r.x : 0;
      console.log(`[lipsync] driving jaw bone "${bone.name}" (baseX=${this.jawBaseX.toFixed(3)})`);
    } else {
      console.warn("[lipsync] no jaw bone found");
    }
    return bone;
  }

  start(remoteStream) {
    this.audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    this.analyser = this.audioCtx.createAnalyser();
    this.analyser.fftSize = 512;
    this.analyser.smoothingTimeConstant = 0.2;
    this.dataArray = new Uint8Array(this.analyser.frequencyBinCount);
    this.source = this.audioCtx.createMediaStreamSource(remoteStream);
    this.source.connect(this.analyser);
    // Analysis only — playback is via the <audio> element.
    if (this.audioCtx.state === "suspended") this.audioCtx.resume().catch(() => {});

    if (this.scene) {
      this.renderObserver = this.scene.onBeforeRenderObservable.add(() => this._frame());
    }
  }

  _frame() {
    if (!this.jaw) {
      this.jaw = this._findJaw();
      if (!this.jaw) { this.renderObserver && this.scene.onBeforeRenderObservable.remove(this.renderObserver); return; }
    }
    // Time-domain RMS = actual loudness envelope (tracks syllables far better
    // than frequency-bin energy, which saturated and held the jaw wide open).
    this.analyser.getByteTimeDomainData(this.dataArray);
    let sum = 0;
    for (let i = 0; i < this.dataArray.length; i++) {
      const v = (this.dataArray[i] - 128) / 128;
      sum += v * v;
    }
    const rms = Math.sqrt(sum / this.dataArray.length);
    // Subtract a small noise floor, then a moderate gain. Speech peaks ~0.15-0.25
    // RMS -> ~0.8-1.0; quiet/silence -> ~0. Rarely pins at 1, so it modulates.
    const target = Math.max(0, Math.min(1, (rms - 0.012) * 5.5));

    // Asymmetric smoothing: open fast, close quickly so the mouth shuts between
    // syllables rather than hanging open.
    const k = target > this.open ? 0.6 : 0.5;
    this.open += (target - this.open) * k;
    if (this.open < 0.02) this.open = 0;   // fully close at rest

    this.jaw.setRotation(new Vector3(this.jawBaseX + this.open * MAX_OPEN, 0, 0));

    // Speech-onset (silence -> speech) cue for a talking gesture.
    if (!this._speaking && this._prevOpen < 0.06 && this.open > 0.16) {
      this._speaking = true;
      if (this.onSpeechStart) { try { this.onSpeechStart(); } catch (e) {} }
    } else if (this._speaking && this.open < 0.04) {
      this._speaking = false;
    }
    this._prevOpen = this.open;

    // Speech-end: fire once after sustained silence following actual speech
    // (used to revert the match-details panel back to Today's Fixtures).
    const now = (typeof performance !== "undefined" ? performance.now() : Date.now());
    if (this.open > 0.06) {
      this._lastVoiceTs = now;
      this._spoke = true;
    } else if (this._spoke && now - this._lastVoiceTs > 1500) {
      this._spoke = false;
      if (this.onSpeechEnd) { try { this.onSpeechEnd(); } catch (e) {} }
    }
  }

  stop() {
    if (this.renderObserver && this.scene) {
      this.scene.onBeforeRenderObservable.remove(this.renderObserver);
      this.renderObserver = null;
    }
    if (this.jaw) {
      try { this.jaw.setRotation(new Vector3(this.jawBaseX, 0, 0)); } catch (e) {}
    }
    if (this.source) this.source.disconnect();
    if (this.audioCtx) this.audioCtx.close();
  }
}
